import { db } from "firebaseConfig";
import { createSharedSnapshotStore } from "utils/sharedSnapshotStore";
import { convertFirestoreDatesToJsDates } from "utils/firestoreConverters";
import { contactSignalDocPath, contactSignalsCollectionPath, toContactSignal } from "./contactDocumentRules";
import type { CandidateSignal, ContactSignal } from "./contactDocumentRules";
import type firebase from "firebase/compat";

/**
 * THE FIRESTORE HALF OF THE SIGNALS — the one module that names the `contact-signals` path.
 *
 * A signal document is `{ v, at, deleted? }` under a book: the server's outbox drain writes
 * one per contact after every change (the server's `contactsSignal.ts`), and clients only
 * read (`firestore.rules`). It is a distinct document per contact on purpose — Firestore's
 * per-document write limit never applies to a busy book, and a card listens to exactly the
 * one document it is about.
 */

export type ContactSignalState = {
  /** The signal, or null when no signal document exists for the contact (yet). */
  signal: ContactSignal | null;
  /** The first snapshot has arrived — `signal: null` is then an answer, not "not yet". */
  ready: boolean;
  /** The listener failed. The last signal is kept; `useContactDocument` polls instead. */
  error: Error | null;
};

const IDLE: ContactSignalState = { signal: null, ready: false, error: null };

/**
 * One listener per (book, contact), shared by every mount that asks about it — the panel,
 * the row's card and a dialog on the same person open one listener, not three — and torn
 * down when the last of them unsubscribes. The key IS the document path.
 */
const signals = createSharedSnapshotStore<ContactSignalState>({
  initialValue: () => IDLE,
  subscribe: (path, emit) => {
    let last: ContactSignalState = IDLE;
    return db.doc(path).onSnapshot(
      (snapshot) => {
        last = {
          signal: snapshot.exists ? toContactSignal(convertFirestoreDatesToJsDates(snapshot.data() ?? {})) : null,
          ready: true,
          error: null
        };
        emit(last);
      },
      (error) => {
        // Keep whatever was last known: a listener error says nothing about the contact.
        emit({ ...last, error: error as Error });
      }
    );
  }
});

export const subscribeContactSignal = (
  bookKey: string,
  contactId: string,
  onChange: (state: ContactSignalState) => void
): (() => void) => signals.subscribe(contactSignalDocPath(bookKey, contactId), onChange);

/**
 * The signals of a phone's possible spellings, in ONE listener — for a card that holds NO
 * contact for the phone and has to hear the day one is created (a call's analysis, an
 * automation, a colleague). A contact's own signal cannot tell it: there is no contact yet,
 * so there is no id to listen to. The Firestore listener this replaces was the same shape, a
 * query by every spelling.
 *
 * Delivers what exists under those spellings each time any of them changes; a document that
 * is not a signal is left out. A listener that FAILS delivers nothing, on purpose: it says
 * nothing about the phone, and the reader's other prompts (the tab coming back, an
 * invalidation) still ask.
 */
export const subscribeCandidateSignals = (
  bookKey: string,
  candidates: readonly string[],
  onChange: (signals: CandidateSignal[]) => void
): (() => void) => {
  if (candidates.length === 0) return () => undefined;
  return db
    .collection(contactSignalsCollectionPath(bookKey))
    .where("__name__", "in", [...candidates])
    .onSnapshot(
      (snapshot) => {
        const found: CandidateSignal[] = [];
        snapshot.docs.forEach((doc) => {
          const signal = toContactSignal(convertFirestoreDatesToJsDates(doc.data() ?? {}));
          if (signal) found.push({ contactId: doc.id, signal });
        });
        onChange(found);
      },
      () => undefined
    );
};

/**
 * Every signal in a book written after `since`, newest first — the old contacts page's live
 * feed in 'server' mode (`AppContext`, `retainContactsLiveFeed`). The same shape as the
 * `updatedTime` feed it replaces, and more honest: a write that carries
 * `skipUpdatedTimeAroundDate` moves no `updatedTime`, but it bumps the version and so
 * writes a signal.
 */
export function getContactSignalsQuery(bookKey: string, since: Date, limit: number): firebase.firestore.Query {
  return db
    .collection(contactSignalsCollectionPath(bookKey))
    .where("at", ">", since)
    .orderBy("at", "desc")
    .limit(limit);
}
